
import Vue from 'vue'
import {SlideData, SlideElement, SlideElementType} from '@/Core/SlideshowData';
import {Component, Prop, Watch} from 'vue-property-decorator';
import {VueInject} from '@/VueIntegration/VueInjectable';
import {Slideshow} from "@/slideshow";
import ClosedCaptions from "./ClosedCaptions.vue";
import {render} from "@/Core/CustomisationRenderer";
import {AnimationPlugin} from "@/Animation/AnimationPlugin";
import {PubSub} from "@/Core/PubSub";
import {TutorialState} from "@/Core/TutorialState";
import {CreatorEvents} from "@/CreatorTools/Services/CreatorEvents";

@Component({
    components: {ClosedCaptions}
})
export default class Slide extends Vue {
    @Prop({required: true, type: String})
    id: string;

    @Prop({type: Number, default: 0}) width: number;
    @Prop({type: Number, default: 0}) height: number;

    @Prop({type: Boolean}) isCreatorModeEnabled: boolean;

    @Prop({required: false, default: false, type: Boolean})
    isLegacy: boolean;

    @VueInject("Slideshow")
    slideshow: Slideshow;
    slideData: SlideData|null = null;

    @VueInject("PubSub") pubSub: PubSub;
    @VueInject("AnimationPlugin") animationPlugin: AnimationPlugin;
    @VueInject("TutorialState") state: TutorialState;

    private handles: number[] = [];

    created() {
        this.handles = [
            this.pubSub.subscribeToEvent(CreatorEvents.AddSlideElement, ({slideId, elementId}) => {
                if (this.slideshow.currentSlide.id !== slideId)
                    return;

                let index = this.slideshow.currentSlide.elements.findIndex(e => e.id === elementId);
                return this.insertSlideElements([this.slideshow.currentSlide.elements[index]]);
            }),
            this.pubSub.subscribeToEvent(CreatorEvents.DeleteSlideElement, (data: any) => {
                if (this.slideshow.currentSlide.id === data.slideId)
                    return this.deleteSlideElement(data.elementId);
            }),
        ];
    }

    beforeDestroy() {
        for (let h of this.handles)
            this.pubSub.unsubscribeToEvent(h);
    }

    mounted() {
        this.enter();
    }

    @Watch("isCreatorModeEnabled")
    reload(){
        this.$nextTick(() => {
            this.animationPlugin.reloadTimelines();
        });
    }

    @Watch("id")
    enter() {
        this.slideData = this.slideshow.currentSlide;
        let animContainer = <HTMLElement> this.$refs['animations'];
        this.animationPlugin.animationContainer = animContainer;
        animContainer.innerHTML = render(this.slideData.html, this.state);
        animContainer.style.visibility = 'hidden';
        this.insertSlideElements(this.slideData.elements)
            .then(() => {
                let queryResult = animContainer.querySelectorAll("a[href]");
                for (let i = 0; i < queryResult.length; i++) {
                    let element = <HTMLAnchorElement> queryResult.item(i);
                    if (element.getAttribute("href")![0] === '#') {
                        element.addEventListener('click', event => {
                            this.slideshow.nextSlide(element.getAttribute("href")!.substr(1));
                            event.preventDefault();
                            event.stopPropagation();
                        });
                    }
                    else if (element.getAttribute("href")![0] === '!') {
                        element.addEventListener('click', event => {
                            let href = element.getAttribute("href");
                            this.slideshow.goBackTo(href!.substr(1));
                            event.preventDefault();
                            event.stopPropagation();
                        });
                    }
                }

                animContainer.style.visibility = 'visible';
                this.slideshow.slideEntered();
            });
    }

    handleClick(event: MouseEvent) {
        if (!this.isCreatorModeEnabled)
            return;

        let target = <HTMLElement> event.target;
        if (target === this.$refs['animations'])
            this.pubSub.publishEvent(CreatorEvents.SelectKeyFrames, []);
        else
            this.pubSub.publishEvent(CreatorEvents.SelectSlideElements, [target.id]);
    }

    insertSlideElements(elements: SlideElement[]): Promise<void> {
        let promises = elements.map(e => this.insertSlideElement(e));
        let animContainer = <HTMLElement> this.$refs['animations'];
        return Promise.all(promises)
            .then(elements => {
                for (let e of elements) {
                    if (e != null)
                        animContainer.appendChild(e);
                }
            });
    }

    insertSlideElement(e: SlideElement): Promise<HTMLElement|null> {
        let element: Promise<HTMLElement|null>;

        if (e.type === SlideElementType.Existing)
            return Promise.resolve(null);

        if (e.type === SlideElementType.Image) {
            element = this.slideshow.assets.loadImageAsset(e.src)
                .then(el => {
                    if (el == null)
                        return null;

                    if (e.width != null) {
                        el.style.width = e.width + "rem";
                    }

                    if (e.height != null) {
                        el.style.height = e.height + "rem";
                    }

                    el.id = e.id;
                    return el;
                });
        }
        else if (e.type === SlideElementType.Text) {
            let div = document.createElement("div");
            div.textContent = e.text;
            div.style.fontSize = e.fontSize + "rem";
            if (e.weight) {
                div.style.fontWeight = e.weight.toString();
            }
            div.id = e.id;
            element = Promise.resolve(div);
        }
        else {
            throw "Unknown Element";
        }

        return element;
    }

    deleteSlideElement(elementId: string) {
        let animContainer = <HTMLElement> this.$refs['animations'];
        let element = document.getElementById(elementId);
        if (element)
            animContainer.removeChild(element);
    }
}
